import { useCallback } from 'react';

import { useFastshiftAnalyticsData, useFireAnalyticsEvents } from '@/analytics';
import { ActionKey, ActionSubjectKey, ActionSubjectIdKey } from '@/analytics/constants';

export const useMigrationTicketsUIAnalytics = () => {
	const { sourceKey, identityAttrs } = useFastshiftAnalyticsData(true);
	const { fireUIEvent } = useFireAnalyticsEvents(identityAttrs);

	const sendOrgSelectorDropdownClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.DROPDOWN,
			actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
			source: sourceKey,
		});
	}, [fireUIEvent, sourceKey]);

	const sendOrgSelectorDropdownItemClickUIEvent = useCallback(
		(selectedOrgId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
				source: sourceKey,
				attributes: {
					selectedOrgId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendStatusFilterDropdownClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.DROPDOWN,
			actionSubjectId: ActionSubjectIdKey.STATUS_FILTER_DROPDOWN,
			source: sourceKey,
		});
	}, [fireUIEvent, sourceKey]);

	const sendStatusFilterDropdownItemClickUIEvent = useCallback(
		(selectedStatus: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.STATUS_FILTER_DROPDOWN,
				source: sourceKey,
				attributes: {
					selectedStatus,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendMigrationPhaseDropdownClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.DROPDOWN,
			actionSubjectId: ActionSubjectIdKey.MIGRATION_PHASE_DROPDOWN,
			source: sourceKey,
		});
	}, [fireUIEvent, sourceKey]);

	const sendMigrationPhaseDropdownItemClickUIEvent = useCallback(
		(selectedMigrationPhase: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.MIGRATION_PHASE_DROPDOWN,
				source: sourceKey,
				attributes: {
					selectedMigrationPhase,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendClearFiltersButtonClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.BUTTON,
			actionSubjectId: ActionSubjectIdKey.CLEAR_FILTERS_BUTTON,
			source: sourceKey,
		});
	}, [fireUIEvent, sourceKey]);

	const sendMigrationTicketsTableOpenSupportTicketClickUIEvent = useCallback(
		(ticketId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.MIGRATION_TICKETS_TABLE_OPEN_SUPPORT_TICKET_LINK,
				source: sourceKey,
				attributes: {
					ticketId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendRelatedTicketsRowExpandToggleUIEvent = useCallback(
		(supportTicketId: string, isExpanded: boolean, relatedTicketsCount: number) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.RELATED_TICKETS_ROW_EXPAND_TOGGLE,
				source: sourceKey,
				attributes: {
					supportTicketId,
					isExpanded,
					relatedTicketsCount,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendRelatedTicketsRowOpenSupportTicketLinkClickUIEvent = useCallback(
		(supportTicketId: string, relatedTicketId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.RELATED_TICKETS_ROW_OPEN_SUPPORT_TICKET_LINK,
				source: sourceKey,
				attributes: {
					supportTicketId,
					relatedTicketId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendViewRunbooksButtonClickUIEvent = useCallback(
		(supportTicketId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.BUTTON,
				actionSubjectId: ActionSubjectIdKey.VIEW_RUNBOOKS_BUTTON,
				source: sourceKey,
				attributes: {
					supportTicketId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	return {
		sendOrgSelectorDropdownClickUIEvent,
		sendOrgSelectorDropdownItemClickUIEvent,
		sendStatusFilterDropdownClickUIEvent,
		sendStatusFilterDropdownItemClickUIEvent,
		sendMigrationPhaseDropdownClickUIEvent,
		sendMigrationPhaseDropdownItemClickUIEvent,
		sendClearFiltersButtonClickUIEvent,
		sendMigrationTicketsTableOpenSupportTicketClickUIEvent,
		sendRelatedTicketsRowExpandToggleUIEvent,
		sendRelatedTicketsRowOpenSupportTicketLinkClickUIEvent,
		sendViewRunbooksButtonClickUIEvent,
	};
};
